import type { WorkflowGraph } from '../../graph';
import type { OperationNodeSpec } from '../../graph/domain/types';
import type { Store } from '../types';
import { pushUndo } from './historyActions';
import { withSyncGuard } from '../sync/syncFlag';
import { cloneGraphData } from './snapshots';
import {
  planArchetypeConversion,
  type ConversionExtras,
  type ConversionPlan,
  type ConversionTarget,
} from './archetypeConversion/planConversion';
import { applyConversion } from './archetypeConversion/applyConversion';

type SetState = (partial: Partial<Store> | ((s: Store) => Partial<Store>)) => void;
type GetState = () => Store;
type GetGraph = () => WorkflowGraph | null | undefined;

export interface ChangeOperationArchetypeInput {
  operationId: string;
  target: ConversionTarget;
  /** Transfer / complex specifics the target archetype needs. */
  extras?: ConversionExtras;
  /** Form fields (parameters, displayName, checkPoints, …) from the edit modal. */
  patch?: Partial<OperationNodeSpec>;
}

export function createArchetypeConversionActions(
  set: SetState,
  get: GetState,
  getGraph: GetGraph,
) {
  function toSnapshot(graphData: Store['graphData']) {
    return {
      materialNodes: Object.values(graphData.materialNodes),
      operationNodes: Object.values(graphData.operationNodes),
      flowEdges: Object.values(graphData.flowEdges),
      groups: Object.values(graphData.groups),
      schemaVersion: '2.0' as const,
    };
  }

  return {
    /**
     * Preview an archetype change without mutating anything. Used by the edit
     * modal to describe what a conversion will destroy before committing.
     */
    planOperationArchetypeChange(
      operationId: string,
      target: ConversionTarget,
      extras?: ConversionExtras,
    ): ConversionPlan {
      return planArchetypeConversion(get().graphData, operationId, target, extras);
    },

    /**
     * Change an operation's archetype in place: rewire the topology, rebuild the
     * archetype-specific spec fields, clear the stale ones, and re-render.
     *
     * One undo entry covers the whole conversion. The visual layer is rebuilt by
     * a full rehydrate — the only thing that correctly handles an operation
     * moving between a collapsed edge and a COMPLEX vertex.
     */
    changeOperationArchetype(input: ChangeOperationArchetypeInput): void {
      const state = get();
      const operation = state.graphData.operationNodes[input.operationId];
      if (!operation) {
        throw new Error(`Operation not found: ${input.operationId}`);
      }

      const plan = planArchetypeConversion(
        state.graphData,
        input.operationId,
        input.target,
        input.extras,
      );

      if (plan.isNoop) {
        get().editStep(input.operationId, input.patch ?? {});
        return;
      }

      if (plan.blockers.length > 0) {
        throw new Error(plan.blockers.join('\n'));
      }

      pushUndo(
        get,
        set,
        `Change ${operation.displayName ?? 'step'} to ${plan.to.toLowerCase()}`,
      );

      const next = applyConversion(
        cloneGraphData(state.graphData),
        plan,
        input.patch ?? {},
      );

      set((s) => ({
        graphData: next,
        selection: {
          cellIds: s.selection.cellIds.filter(
            (id) =>
              Boolean(next.materialNodes[id]) ||
              Boolean(next.operationNodes[id]) ||
              Boolean(next.flowEdges[id]) ||
              Boolean(next.groups[id]),
          ),
        },
        ui: { ...s.ui, isDirty: true },
      }));

      withSyncGuard(set, () => {
        getGraph()?.hydrateFromSnapshot(toSnapshot(get().graphData));
      });
    },
  };
}
